import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { useRefetchWhenCatalogChanges } from '../contexts/AdminCatalogContext';
import { useAdminUnsaved } from '../contexts/AdminUnsavedContext';
import { useAdminDialog } from '../contexts/AdminDialogContext';
import {
  getTarifarios,
  addTarifario,
  updateTarifario,
  removeTarifario,
  getTarifarioGlobalConfig,
  saveTarifarioGlobalConfig,
  type Tarifario,
  type TarifarioRowItem,
  type TarifarioGlobalConfig,
} from '../lib/experienciasStorage';

type FormRow = { brl: string; usd: string };

/** Linha de transporte: brl + comissão % editável; diariaGuia calculada */
type TransportFormRow = FormRow & { comissaoPct: string };

const emptyRow = (): FormRow => ({ brl: '', usd: '' });

const emptyTransportRow = (): TransportFormRow => ({ brl: '', usd: '', comissaoPct: '' });

const rowFromItem = (item?: TarifarioRowItem): FormRow => ({
  brl: item?.brl != null ? String(item.brl) : '',
  usd: item?.usd != null ? String(item.usd) : '',
});

const transportRowFromItem = (item?: TarifarioRowItem): TransportFormRow => ({
  brl: item?.brl != null ? String(item.brl) : '',
  usd: item?.usd != null ? String(item.usd) : '',
  comissaoPct: item?.comissaoPct != null ? String(Math.round(item.comissaoPct)) : '',
});

const rowToItem = (r: FormRow): TarifarioRowItem => ({
  brl: r.brl.trim() ? parseFloat(r.brl.replace(',', '.')) : undefined,
  usd: r.usd.trim() ? parseFloat(r.usd.replace(',', '.')) : undefined,
});

const transportRowToItem = (r: TransportFormRow): TarifarioRowItem => {
  const brl = r.brl.trim() ? parseFloat(r.brl.replace(',', '.')) : undefined;
  const comissaoPct = r.comissaoPct.trim() ? parseFloat(r.comissaoPct.replace(',', '.')) : undefined;
  return {
    brl: brl ?? undefined,
    usd: r.usd.trim() ? parseFloat(r.usd.replace(',', '.')) : undefined,
    comissaoPct: comissaoPct ?? undefined,
  };
};

/** Linha do passeio exclusivo: Individual + % comissão + % taxa comissão bilíngue (editáveis); demais calculados */
type FormRowPasseio = { brl: string; usd: string; comissaoPct: string; comissaoPctBilingue: string; diariaGuiaUsd: string };

const emptyPasseioRow = (): FormRowPasseio => ({ brl: '', usd: '', comissaoPct: '', comissaoPctBilingue: '', diariaGuiaUsd: '' });

const passeioRowFromItem = (item?: TarifarioRowItem, index = 0): FormRowPasseio => {
  const pax = index + 1;
  const individual = item?.brl;
  const diariaSistema = individual != null ? individual * pax : NaN;
  const diariaGuia = item?.diariaGuiaBrl;
  const comissao = Number.isFinite(diariaSistema) && diariaGuia != null ? diariaSistema - diariaGuia : NaN;
  const comissaoPct = Number.isFinite(diariaSistema) && diariaSistema > 0 && Number.isFinite(comissao)
    ? String(Math.round((comissao / diariaSistema) * 100))
    : '';
  const comissaoPctBilingue = item?.comissaoPctBilingue != null ? String(Math.round(item.comissaoPctBilingue)) : '';
  return {
    brl: item?.brl != null ? String(item.brl) : '',
    usd: item?.usd != null ? String(item.usd) : '',
    comissaoPct,
    comissaoPctBilingue,
    diariaGuiaUsd: item?.diariaGuiaUsd != null ? String(item.diariaGuiaUsd) : '',
  };
};

const passeioRowToItem = (r: FormRowPasseio, index: number): TarifarioRowItem => {
  const num = (s: string) => (s.trim() ? parseFloat(s.replace(',', '.')) : undefined);
  const individual = num(r.brl);
  const pax = index + 1;
  const diariaSistema = individual != null ? individual * pax : NaN;
  const pct = num(r.comissaoPct);
  const comissaoBrl = Number.isFinite(diariaSistema) && pct != null ? diariaSistema * (pct / 100) : NaN;
  const diariaGuiaBrl = Number.isFinite(diariaSistema) && Number.isFinite(comissaoBrl) ? diariaSistema - comissaoBrl : undefined;
  const comissaoPctBilingue = num(r.comissaoPctBilingue);
  return {
    brl: num(r.brl),
    usd: num(r.usd),
    diariaGuiaBrl: Number.isFinite(diariaGuiaBrl) ? diariaGuiaBrl : undefined,
    diariaGuiaUsd: num(r.diariaGuiaUsd),
    comissaoPctBilingue: comissaoPctBilingue ?? undefined,
  };
};

const numStr = (v: number | undefined): string => (v != null ? String(v) : '');

const formatBrl = (v: number | undefined): string =>
  v != null && Number.isFinite(v) ? String(Math.round(v)) : '—';

function parseTaxPctFromConfig(s: string | undefined): number | undefined {
  if (s == null || !String(s).trim()) return undefined;
  const n = parseFloat(String(s).trim().replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
}

/** % taxa nacional PAX 1–4 (passeio exclusivo): linha, tarifário ou global */
function getComissaoPctNacionalPax1to4(t: Tarifario, globalTaxaGeral: string | undefined): number | undefined {
  const row0 = t.passeioExclusivo?.[0];
  const row3 = t.passeioExclusivo?.[3];
  if (row3?.comissaoPct != null && Number.isFinite(row3.comissaoPct)) return row3.comissaoPct;
  if (row0?.comissaoPct != null && Number.isFinite(row0.comissaoPct)) return row0.comissaoPct;
  if (t.taxaGeralAgencia != null && Number.isFinite(t.taxaGeralAgencia)) return t.taxaGeralAgencia;
  return parseTaxPctFromConfig(globalTaxaGeral);
}

function getComissaoPctBilinguePax1to4(t: Tarifario, globalTaxaGeral: string | undefined): number | undefined {
  const row0 = t.passeioExclusivo?.[0];
  const row3 = t.passeioExclusivo?.[3];
  if (row3?.comissaoPctBilingue != null && Number.isFinite(row3.comissaoPctBilingue)) return row3.comissaoPctBilingue;
  if (row0?.comissaoPctBilingue != null && Number.isFinite(row0.comissaoPctBilingue)) return row0.comissaoPctBilingue;
  return getComissaoPctNacionalPax1to4(t, globalTaxaGeral);
}

/** Valores derivados para a lista de tarifários (PAX 4 nacional e bilíngue) */
function computeDashboardPax4ListDerived(t: Tarifario, globalTaxaGeral: string | undefined) {
  const rowPax4 = t.passeioExclusivo?.[3];
  const individualPax4 = rowPax4?.brl;
  const diariaSistemaPax4 =
    individualPax4 != null && Number.isFinite(individualPax4) ? individualPax4 * 4 : undefined;
  const taxaPctNacional = getComissaoPctNacionalPax1to4(t, globalTaxaGeral);
  const taxaPlataformaBrl =
    diariaSistemaPax4 != null && taxaPctNacional != null && Number.isFinite(taxaPctNacional)
      ? diariaSistemaPax4 * (taxaPctNacional / 100)
      : undefined;
  const diariaGuiaNacionalPax4 =
    diariaSistemaPax4 != null && taxaPlataformaBrl != null ? diariaSistemaPax4 - taxaPlataformaBrl : undefined;

  const aditivo = t.aditivoGuiaBilingue;
  const indBil =
    individualPax4 != null && Number.isFinite(individualPax4)
      ? individualPax4 * (1 + (aditivo != null && Number.isFinite(aditivo) ? aditivo : 0) / 100)
      : undefined;
  const diariaSistemaBil = indBil != null && Number.isFinite(indBil) ? indBil * 4 : undefined;
  const pctBil = getComissaoPctBilinguePax1to4(t, globalTaxaGeral);
  const comissaoBilBrl =
    diariaSistemaBil != null && pctBil != null && Number.isFinite(pctBil) ? diariaSistemaBil * (pctBil / 100) : undefined;
  const diariaGuiaBilinguePax4 =
    diariaSistemaBil != null && comissaoBilBrl != null ? diariaSistemaBil - comissaoBilBrl : undefined;

  return {
    taxaPctNacional,
    taxaPlataformaBrl,
    diariaGuiaNacionalPax4,
    diariaGuiaBilinguePax4,
  };
}

const parseBrl = (s: string): number | undefined => {
  const v = s.trim().replace(',', '.');
  if (!v) return undefined;
  const n = parseFloat(v);
  return isNaN(n) ? undefined : n;
};

/** Calcula os valores Individual para cada linha do transporte (arredonda para cima) */
function computeTransportIndividuals(form: FormState): { value: string; num: number }[] {
  const brl = (k: keyof FormState) => parseBrl((form[k] as TransportFormRow)?.brl ?? '') ?? NaN;
  const ind3 = Number.isFinite(brl('veiculo4x4_4')) ? Math.ceil(brl('veiculo4x4_4') / 4) : NaN;
  const ind1 = Number.isFinite(brl('excursaoIndividualTranslado')) ? Math.ceil(brl('excursaoIndividualTranslado')) : NaN;
  const ind2 = Number.isFinite(brl('veiculo4x4_6')) ? Math.ceil(brl('veiculo4x4_6') / 6) : NaN;
  const ind4 = Number.isFinite(brl('veiculoPasseio')) ? Math.ceil(brl('veiculoPasseio') / 4) : NaN;
  const ind5 = Number.isFinite(brl('taxi')) ? Math.ceil(brl('taxi') / 4) : NaN;
  const ind6 = Number.isFinite(brl('van15')) ? Math.ceil(brl('van15') / 16) : NaN;
  const ind7 = Number.isFinite(brl('van20')) ? Math.ceil(brl('van20') / 22) : NaN;
  const motoBrl = parseBrl(form.deslocamentoMoto.brl ?? '') ?? NaN;
  const indMoto = Number.isFinite(motoBrl) ? Math.ceil(motoBrl) : NaN;
  const fmt = (n: number) => (Number.isFinite(n) ? String(Math.round(n)) : '—');
  // Ordem: Individual, Taxi, 4x4 (5 lug), Passeio, 4x4 (7 lug), Van 16, Van 22, Moto (deslocamento)
  return [
    { value: fmt(ind1), num: ind1 },
    { value: fmt(ind5), num: ind5 },
    { value: fmt(ind3), num: ind3 },
    { value: fmt(ind4), num: ind4 },
    { value: fmt(ind2), num: ind2 },
    { value: fmt(ind6), num: ind6 },
    { value: fmt(ind7), num: ind7 },
    { value: fmt(indMoto), num: indMoto },
  ];
}

/** Componente fora do pai para evitar recriação e perda de foco nos inputs */
function TarifaRow({
  label,
  brl,
  onBrlChange,
  hint,
  threeCols,
}: {
  label: string;
  brl: string;
  onBrlChange: (v: string) => void;
  hint?: string;
  threeCols?: boolean;
}) {
  const gridClass = threeCols ? 'grid-cols-[1.5fr_6rem_1fr]' : 'grid-cols-[2fr_1fr]';
  return (
    <div className={`grid gap-2 w-full min-w-0 items-center ${gridClass}`}>
      <label className="text-sm font-medium text-gray-700 truncate min-w-0 py-2 px-3">{label}</label>
      <input
        type="text"
        inputMode="decimal"
        placeholder=""
        value={brl}
        onChange={(e) => onBrlChange(e.target.value)}
        className="w-full min-w-0 py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm"
      />
      {threeCols && (
        <span className="text-xs text-blue-600 py-2 px-2 self-center whitespace-nowrap min-w-max">{hint ?? ''}</span>
      )}
    </div>
  );
}

function SectionHeaderRow({ threeCols }: { threeCols?: boolean } = {}) {
  return (
    <div className={`grid gap-2 w-full min-w-0 ${threeCols ? 'grid-cols-[1.5fr_6rem_1fr]' : 'grid-cols-[2fr_1fr]'}`}>
      <div className="py-2 px-3 font-semibold text-gray-800 text-sm" />
      <div className="py-2 px-3 font-semibold text-gray-800 text-sm text-center" />
      {threeCols && <div />}
    </div>
  );
}

/** Grid: Label | Valor Plataforma BRL | Taxa % | Pagamento Guia/Motorista BRL | Individual BRL | Individual PG/Lucro BRL */
const TRANSPORT_GRID = 'grid-cols-[minmax(0,max-content)_5rem_5rem_minmax(7rem,1fr)_6rem_minmax(7rem,1.1fr)]';

function TransportSectionHeader() {
  return (
    <div className="contents">
      <div className="py-2 px-3 font-semibold text-gray-800 text-sm" />
      <div className="py-2 px-3 font-semibold text-gray-800 text-sm text-right">Valor Plataforma BRL</div>
      <div className="py-2 px-3 font-semibold text-gray-800 text-sm text-right">Taxa %</div>
      <div className="py-2 px-3 font-semibold text-red-600 text-sm text-right leading-tight">Pagamento Guia/Motorista BRL</div>
      <div className="py-2 px-3 font-semibold text-gray-800 text-sm text-right">Individual BRL</div>
      <div className="py-2 px-3 font-semibold text-gray-800 text-sm text-right leading-tight">Individual PG/Lucro BRL</div>
    </div>
  );
}

function TarifaTransportRow({
  label,
  brl,
  comissaoPct,
  individual,
  individualNum,
  onBrlChange,
  onComissaoPctChange,
  highlightPlataformaBrl,
}: {
  label: string;
  brl: string;
  comissaoPct: string;
  individual: string;
  individualNum: number;
  onBrlChange: (v: string) => void;
  onComissaoPctChange: (v: string) => void;
  /** Destaque azul na coluna Valor Plataforma BRL (referência para equalização). */
  highlightPlataformaBrl?: boolean;
}) {
  const brlNum = brl.trim() ? parseFloat(brl.replace(',', '.')) : NaN;
  const pctNum = comissaoPct.trim() ? parseFloat(comissaoPct.replace(',', '.')) : NaN;
  const diariaGuia = Number.isFinite(brlNum) && Number.isFinite(pctNum)
    ? brlNum * (1 - pctNum / 100)
    : NaN;
  const diariaGuiaStr = Number.isFinite(diariaGuia) ? String(Math.round(diariaGuia)) : '—';
  const individualComTaxa = Number.isFinite(individualNum) && Number.isFinite(pctNum)
    ? individualNum * (1 - pctNum / 100)
    : NaN;
  const valorDaTaxa = Number.isFinite(individualNum) && Number.isFinite(pctNum)
    ? individualNum * (pctNum / 100)
    : NaN;
  const individualComTaxaStr = Number.isFinite(individualComTaxa) ? String(Math.round(individualComTaxa)) : '—';
  const valorDaTaxaStr = Number.isFinite(valorDaTaxa) ? String(Math.round(valorDaTaxa)) : '—';
  return (
    <div className="contents">
      <label className="text-sm font-medium text-gray-700 truncate min-w-0 py-2 px-3">{label}</label>
      <input
        type="text"
        inputMode="decimal"
        placeholder=""
        value={brl}
        onChange={(e) => onBrlChange(e.target.value)}
        className={
          highlightPlataformaBrl
            ? 'w-full min-w-0 py-2 px-3 rounded border border-blue-400 bg-blue-50 text-blue-900 font-medium focus:ring-2 focus:ring-blue-400 focus:border-blue-500 text-sm text-right'
            : 'w-full min-w-0 py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm text-right'
        }
      />
      <input
        type="text"
        inputMode="decimal"
        placeholder="%"
        value={comissaoPct}
        onChange={(e) => onComissaoPctChange(e.target.value)}
        className="w-full min-w-0 py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm text-right"
      />
      <div className="py-2 px-3 text-sm text-red-600 bg-gray-100 rounded border border-gray-200 text-right font-medium min-w-0">
        {diariaGuiaStr}
      </div>
      <div className="py-2 px-3 text-sm text-gray-600 bg-gray-100 rounded border border-gray-200 text-right font-medium min-w-0">
        {individual}
      </div>
      <div
        className="py-2 px-3 text-sm bg-gray-100 rounded border border-gray-200 text-right font-medium min-w-0"
        title="PG/Lucro por pessoa após taxa %. Entre parênteses: valor da taxa (Individual BRL × taxa %)."
      >
        <span className="text-red-600">{individualComTaxaStr}</span>
        {' '}
        <span className="text-blue-600">({valorDaTaxaStr})</span>
      </div>
    </div>
  );
}

const INIT_FORM = {
  nome: '',
  descricao: '',
  taxaGeralAgencia: '',
  taxaMinimaAgencia: '',
  taxaMaximaAgencia: '',
  excursaoIndividual: emptyRow(),
  excursaoIndividualTranslado: emptyTransportRow(),
  veiculo4x4_6: emptyTransportRow(),
  veiculo4x4_4: emptyTransportRow(),
  veiculoPasseio: emptyTransportRow(),
  van15: emptyTransportRow(),
  van20: emptyTransportRow(),
  taxi: emptyTransportRow(),
  deslocamentoMoto: emptyTransportRow(),
  passeioExclusivo: Array.from({ length: 12 }, () => emptyPasseioRow()),
  descontoIdoso: '',
  descontoAdolescenteCrianca: '',
  idadeMaxCriancaNaoPaga: '',
  aditivoGuiaBilingue: '',
  diariaMinima: '',
} as const;

type FormState = {
  nome: string;
  descricao: string;
  taxaGeralAgencia: string;
  taxaMinimaAgencia: string;
  taxaMaximaAgencia: string;
  excursaoIndividual: FormRow;
  excursaoIndividualTranslado: TransportFormRow;
  veiculo4x4_6: TransportFormRow;
  veiculo4x4_4: TransportFormRow;
  veiculoPasseio: TransportFormRow;
  van15: TransportFormRow;
  van20: TransportFormRow;
  taxi: TransportFormRow;
  deslocamentoMoto: TransportFormRow;
  passeioExclusivo: FormRowPasseio[];
  descontoIdoso: string;
  descontoAdolescenteCrianca: string;
  idadeMaxCriancaNaoPaga: string;
  aditivoGuiaBilingue: string;
  diariaMinima: string;
};

const TRANSPORT_KEYS: (keyof FormState)[] = [
  'excursaoIndividualTranslado', 'veiculo4x4_6', 'veiculo4x4_4', 'veiculoPasseio',
  'van15', 'van20', 'taxi', 'deslocamentoMoto',
];

const GLOBAL_CONFIG_KEYS = [
  'taxaGeralAgencia',
  'taxaMinimaAgencia',
  'taxaMaximaAgencia',
  'descontoIdoso',
  'descontoCrianca',
  'descontoAdolescente',
  'descontoGuia',
  'descontoVeiculo',
  'descontoBebe',
  'taxaServicoIngresso',
  'pisoDiariaGuia',
  'premioMensalPrimeiro',
  'premioMensalSegundo',
  'premioMensalTerceiro',
  'premioAnualPrimeiro',
  'premioAnualSegundo',
  'premioAnualTerceiro',
] as const;

type GlobalConfigForm = Record<(typeof GLOBAL_CONFIG_KEYS)[number], string>;

function globalConfigToForm(c: TarifarioGlobalConfig): GlobalConfigForm {
  return {
    taxaGeralAgencia: numStr(c.taxaGeralAgencia),
    taxaMinimaAgencia: numStr(c.taxaMinimaAgencia),
    taxaMaximaAgencia: numStr(c.taxaMaximaAgencia),
    descontoIdoso: numStr(c.descontoIdoso),
    descontoCrianca: numStr(c.descontoCrianca ?? c.descontoAdolescenteCrianca ?? 50),
    descontoAdolescente: numStr(c.descontoAdolescente ?? 0),
    descontoGuia: numStr(c.descontoGuia ?? c.descontoCompartilhado),
    descontoVeiculo: numStr(c.descontoVeiculo ?? 10),
    descontoBebe: numStr(c.descontoBebe ?? 100),
    taxaServicoIngresso: numStr(c.taxaServicoIngresso),
    pisoDiariaGuia: numStr(c.pisoDiariaGuia ?? 250),
    premioMensalPrimeiro: numStr(c.premiacaoRanking?.mensal?.primeiro),
    premioMensalSegundo: numStr(c.premiacaoRanking?.mensal?.segundo),
    premioMensalTerceiro: numStr(c.premiacaoRanking?.mensal?.terceiro),
    premioAnualPrimeiro: numStr(c.premiacaoRanking?.anual?.primeiro),
    premioAnualSegundo: numStr(c.premiacaoRanking?.anual?.segundo),
    premioAnualTerceiro: numStr(c.premiacaoRanking?.anual?.terceiro),
  };
}

function formToGlobalConfig(f: GlobalConfigForm): TarifarioGlobalConfig {
  const parse = (s: string | undefined): number | undefined => {
    if (s == null || typeof s !== 'string') return undefined;
    const v = s.trim().replace(',', '.');
    if (!v) return undefined;
    const n = parseFloat(v);
    return isNaN(n) ? undefined : n;
  };
  return {
    taxaGeralAgencia: parse(f.taxaGeralAgencia),
    taxaMinimaAgencia: parse(f.taxaMinimaAgencia),
    taxaMaximaAgencia: parse(f.taxaMaximaAgencia),
    descontoIdoso: parse(f.descontoIdoso),
    descontoCrianca: parse(f.descontoCrianca),
    descontoAdolescente: parse(f.descontoAdolescente),
    descontoGuia: parse(f.descontoGuia),
    descontoVeiculo: parse(f.descontoVeiculo),
    descontoBebe: parse(f.descontoBebe),
    taxaServicoIngresso: parse(f.taxaServicoIngresso),
    pisoDiariaGuia: parse(f.pisoDiariaGuia),
    premiacaoRanking: {
      mensal: {
        primeiro: parse(f.premioMensalPrimeiro),
        segundo: parse(f.premioMensalSegundo),
        terceiro: parse(f.premioMensalTerceiro),
      },
      anual: {
        primeiro: parse(f.premioAnualPrimeiro),
        segundo: parse(f.premioAnualSegundo),
        terceiro: parse(f.premioAnualTerceiro),
      },
    },
  };
}

export default function TarifariosPage() {
  const [list, setList] = useState<Tarifario[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { setDirty } = useAdminUnsaved();
  const { showConfirm, showAlert } = useAdminDialog();
  const isFirstFormRenderRef = useRef(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState<FormState>({ ...INIT_FORM });
  const [globalConfig, setGlobalConfig] = useState<GlobalConfigForm>(() => globalConfigToForm(getTarifarioGlobalConfig()));
  const [formTab, setFormTab] = useState<'tarifario' | 'transporte' | 'nacional' | 'bilingue'>('tarifario');
  const [globalSectionTab, setGlobalSectionTab] = useState<'configuracoes' | 'premiacoes'>('configuracoes');

  const refresh = () => {
    setList(getTarifarios());
    setGlobalConfig(globalConfigToForm(getTarifarioGlobalConfig()));
  };

  useEffect(() => {
    refresh();
  }, []);

  useRefetchWhenCatalogChanges(() => refresh());

  useEffect(() => {
    const state = location.state as { fromNav?: boolean } | null;
    if (state?.fromNav && location.pathname === '/tarifarios') {
      setDirty(false);
      setModalOpen(false);
      setEditingId(null);
      setError('');
      isFirstFormRenderRef.current = true;
      navigate('/tarifarios', { replace: true, state: {} });
    }
  }, [location.pathname, location.state, setDirty, navigate]);

  useEffect(() => {
    if (!modalOpen) {
      isFirstFormRenderRef.current = true;
      return;
    }
    if (isFirstFormRenderRef.current) {
      isFirstFormRenderRef.current = false;
      return;
    }
    setDirty(true);
  }, [form, modalOpen, setDirty]);

  const PISO_DIARIA_GUIA_MINIMO = 250;

  const saveGlobalConfig = useCallback((next: GlobalConfigForm) => {
    try {
      const cfg = formToGlobalConfig(next);
      const piso = cfg.pisoDiariaGuia;
      if (piso != null && Number.isFinite(piso) && piso < PISO_DIARIA_GUIA_MINIMO) {
        cfg.pisoDiariaGuia = PISO_DIARIA_GUIA_MINIMO;
        setGlobalConfig((prev) => ({ ...prev, pisoDiariaGuia: String(PISO_DIARIA_GUIA_MINIMO) }));
        showAlert(`PISO Diária GUIA deve ser no mínimo R$ ${PISO_DIARIA_GUIA_MINIMO}. Valor ajustado.`, 'Aviso', 'Entendi');
      }
      saveTarifarioGlobalConfig(cfg);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erro ao salvar configurações globais.');
    }
  }, [showAlert]);

  const updateGlobalConfig = useCallback((key: (typeof GLOBAL_CONFIG_KEYS)[number], value: string) => {
    setGlobalConfig((prev) => {
      const next = { ...prev, [key]: value };
      saveGlobalConfig(next);
      return next;
    });
  }, [saveGlobalConfig]);

  const getFormFromTarifario = useCallback((t: Tarifario): FormState => ({
    nome: t.nome,
    descricao: t.descricao ?? '',
    taxaGeralAgencia: numStr(t.taxaGeralAgencia),
    taxaMinimaAgencia: numStr(t.taxaMinimaAgencia),
    taxaMaximaAgencia: numStr(t.taxaMaximaAgencia),
    excursaoIndividual: rowFromItem(t.excursaoIndividual),
    excursaoIndividualTranslado: transportRowFromItem(t.excursaoIndividualTranslado),
    veiculo4x4_6: transportRowFromItem(t.veiculo4x4_6),
    veiculo4x4_4: transportRowFromItem(t.veiculo4x4_4),
    veiculoPasseio: transportRowFromItem(t.veiculoPasseio),
    van15: transportRowFromItem(t.van15),
    van20: transportRowFromItem(t.van20),
    taxi: transportRowFromItem(t.taxi),
    deslocamentoMoto: transportRowFromItem(t.deslocamentoMoto),
    passeioExclusivo: (() => {
      const arr = t.passeioExclusivo ?? [];
      const rows = arr.slice(0, 12).map((item, i) => passeioRowFromItem(item, i));
      while (rows.length < 12) rows.push(emptyPasseioRow());
      return rows.slice(0, 12);
    })(),
    descontoIdoso: numStr(t.descontoIdoso),
    descontoAdolescenteCrianca: numStr(t.descontoAdolescenteCrianca),
    idadeMaxCriancaNaoPaga: numStr(t.idadeMaxCriancaNaoPaga),
    aditivoGuiaBilingue: numStr(t.aditivoGuiaBilingue),
    diariaMinima: (() => {
      const row4 = t.passeioExclusivo?.[3];
      const ind = row4?.brl;
      return ind != null ? String(Math.round(ind * 4)) : '';
    })(),
  }), []);

  const openNew = useCallback(() => {
    isFirstFormRenderRef.current = true;
    setDirty(false);
    setEditingId(null);
    const baseForm = JSON.parse(JSON.stringify(INIT_FORM)) as FormState;
    const taxaAgencia = globalConfig.taxaGeralAgencia || '';
    const passeioComComissao = baseForm.passeioExclusivo.map((row) => ({
      ...row,
      comissaoPct: taxaAgencia,
      comissaoPctBilingue: taxaAgencia,
    }));
    const transportComComissao = { ...baseForm };
    for (const key of TRANSPORT_KEYS) {
      const row = transportComComissao[key];
      if (row && typeof row === 'object' && 'comissaoPct' in row) {
        (transportComComissao as Record<string, unknown>)[key] = { ...row, comissaoPct: taxaAgencia };
      }
    }
    setForm({
      ...baseForm,
      ...globalConfig,
      ...transportComComissao,
      passeioExclusivo: passeioComComissao,
    });
    setError('');
    setFormTab('tarifario');
    setModalOpen(true);
  }, [globalConfig, setDirty]);

  const openEdit = useCallback(
    (t: Tarifario) => {
      isFirstFormRenderRef.current = true;
      setDirty(false);
      setEditingId(t.id);
      setForm({ ...getFormFromTarifario(t), ...globalConfig });
      setError('');
      setFormTab('tarifario');
      setModalOpen(true);
    },
    [getFormFromTarifario, globalConfig, setDirty]
  );

  const closeModal = useCallback(() => {
    setDirty(false);
    setModalOpen(false);
    setEditingId(null);
    setError('');
  }, [setDirty]);

  const updateForm = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateRow = useCallback((key: keyof FormState, field: 'brl' | 'usd' | 'comissaoPct', value: string) => {
    setForm((prev) => {
      const row = prev[key];
      if (typeof row !== 'object' || row === null || !('brl' in row)) return prev;
      return { ...prev, [key]: { ...row, [field]: value } };
    });
  }, []);

  /** Preenche BRL dos modos de transporte a partir de «Individual com Translado»; comissões % mantidas; campos editáveis depois. */
  const handleEqualizarPrecosTransporte = useCallback(() => {
    const raw = form.excursaoIndividualTranslado.brl.trim();
    const i = raw ? parseFloat(raw.replace(',', '.')) : NaN;
    if (!Number.isFinite(i) || i <= 0) {
      void showAlert(
        'Preencha um valor válido em «Individual com Translado» antes de equalizar os preços.',
        'Aviso',
        'Entendi'
      );
      return;
    }
    const fmt = (n: number) => String(Math.round(n));
    setForm((prev) => ({
      ...prev,
      taxi: { ...prev.taxi, brl: fmt(i * 2) },
      veiculo4x4_4: { ...prev.veiculo4x4_4, brl: fmt(i * 4 - i / 2) },
      veiculoPasseio: { ...prev.veiculoPasseio, brl: fmt(i * 3) },
      veiculo4x4_6: { ...prev.veiculo4x4_6, brl: fmt(i * 5) },
      deslocamentoMoto: { ...prev.deslocamentoMoto, brl: fmt(i) },
    }));
    setDirty(true);
  }, [form.excursaoIndividualTranslado.brl, showAlert, setDirty]);

  const handleDiariaMinimaBlur = useCallback(() => {
    const pisoVal = globalConfig.pisoDiariaGuia?.trim() ? parseFloat(String(globalConfig.pisoDiariaGuia).replace(',', '.')) : NaN;
    const pisoDiariaGuia = Math.max(PISO_DIARIA_GUIA_MINIMO, Number.isFinite(pisoVal) ? pisoVal : PISO_DIARIA_GUIA_MINIMO);
    const val = form.diariaMinima.trim() ? parseFloat(form.diariaMinima.replace(',', '.')) : NaN;
    if (Number.isFinite(val) && val < pisoDiariaGuia) {
      updateForm('diariaMinima', String(Math.round(pisoDiariaGuia)));
      setError('');
      showAlert(`Diária Mínima não pode ser menor que o PISO Diária GUIA (R$ ${pisoDiariaGuia}). Valor ajustado.`, 'Aviso', 'Entendi');
    }
  }, [form.diariaMinima, globalConfig.pisoDiariaGuia, updateForm, showAlert]);

  const handleExcursaoIndividualBlur = useCallback(() => {
    const n = form.diariaMinima.trim() ? parseFloat(form.diariaMinima.replace(',', '.')) : NaN;
    const max = Number.isFinite(n) ? n / 4 - 8 : Infinity;
    const val = form.excursaoIndividual.brl.trim() ? parseFloat(form.excursaoIndividual.brl.replace(',', '.')) : NaN;
    if (Number.isFinite(val) && val >= max) {
      const maxExibir = Math.round(max);
      const valorCorreto = Math.floor(max - 0.0001);
      updateRow('excursaoIndividual', 'brl', String(valorCorreto));
      setError(`Individual PAX 5 Desconto Grupo deve ser menor que ${maxExibir}. Valor ajustado para ${valorCorreto}.`);
      showAlert(`Individual PAX 5 Desconto Grupo deve ser menor que ${maxExibir}. O valor foi ajustado automaticamente para ${valorCorreto}.`, 'Aviso', 'Entendi');
    }
  }, [form.diariaMinima, form.excursaoIndividual.brl, updateRow, showAlert]);

  const updatePasseioRow = useCallback((index: number, field: 'brl' | 'usd' | 'comissaoPct' | 'comissaoPctBilingue' | 'diariaGuiaUsd', value: string) => {
    setForm((prev) => {
      const next = [...prev.passeioExclusivo];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, passeioExclusivo: next };
    });
  }, []);

  const updatePasseioRowsPax1To4 = useCallback((field: 'comissaoPct' | 'comissaoPctBilingue', value: string) => {
    setForm((prev) => {
      const next = prev.passeioExclusivo.map((row, i) =>
        i < 4 ? { ...row, [field]: value } : row
      );
      return { ...prev, passeioExclusivo: next };
    });
  }, []);

  const getIndividualForPax = useCallback((pax: number) => {
    const diariaMinimaNum = form.diariaMinima.trim() ? parseFloat(form.diariaMinima.replace(',', '.')) : NaN;
    const excursaoIndividualNum = form.excursaoIndividual.brl.trim() ? parseFloat(form.excursaoIndividual.brl.replace(',', '.')) : NaN;
    return pax <= 4 && Number.isFinite(diariaMinimaNum)
      ? diariaMinimaNum / pax
      : pax >= 5 && Number.isFinite(excursaoIndividualNum)
        ? excursaoIndividualNum
        : NaN;
  }, [form.diariaMinima, form.excursaoIndividual.brl]);

  const validateComissaoPax = useCallback(
    (index: number, newPct: number, variant: 'nacional' | 'bilingue') => {
      if (index === 0) return { valid: true as const };
      const taxaPct = form.taxaGeralAgencia.trim() ? parseFloat(form.taxaGeralAgencia.replace(',', '.')) : NaN;
      const prevRow = form.passeioExclusivo[index - 1];
      const prevPctStr = variant === 'nacional' ? prevRow.comissaoPct : prevRow.comissaoPctBilingue;
      const prevPct = prevPctStr.trim() ? parseFloat(prevPctStr.replace(',', '.')) : (Number.isFinite(taxaPct) ? taxaPct : NaN);
      const pax = index + 1;
      const prevPax = index;
      const indNac = getIndividualForPax(pax);
      const indPrevNac = getIndividualForPax(prevPax);
      const aditivoPct = form.aditivoGuiaBilingue.trim() ? parseFloat(form.aditivoGuiaBilingue.replace(',', '.')) : NaN;
      const indBil = Number.isFinite(indNac) && Number.isFinite(aditivoPct) ? indNac * (1 + aditivoPct / 100) : NaN;
      const indPrevBil = Number.isFinite(indPrevNac) && Number.isFinite(aditivoPct) ? indPrevNac * (1 + aditivoPct / 100) : NaN;
      const dsNac = Number.isFinite(indNac) ? indNac * pax : NaN;
      const dsPrevNac = Number.isFinite(indPrevNac) ? indPrevNac * prevPax : NaN;
      const dsBil = Number.isFinite(indBil) ? indBil * pax : NaN;
      const dsPrevBil = Number.isFinite(indPrevBil) ? indPrevBil * prevPax : NaN;
      const diariaSistema = variant === 'nacional' ? dsNac : dsBil;
      const diariaSistemaPrev = variant === 'nacional' ? dsPrevNac : dsPrevBil;
      const prevDiariaGuia = Number.isFinite(diariaSistemaPrev) && prevPct != null && Number.isFinite(prevPct)
        ? diariaSistemaPrev * (1 - prevPct / 100)
        : NaN;
      const diariaGuiaNew = Number.isFinite(diariaSistema) ? diariaSistema * (1 - newPct / 100) : NaN;
      if (prevPct != null && Number.isFinite(prevPct) && newPct < prevPct) {
        const revertTo = String(Math.round(prevPct * 10) / 10);
        return { valid: false as const, rule: 1 as const, revertTo, message: `A comissão do PAX ${pax} deve ser maior ou igual à comissão do PAX ${prevPax}. Mínimo permitido: ${revertTo}%` };
      }
      if (Number.isFinite(prevDiariaGuia) && Number.isFinite(diariaGuiaNew) && diariaGuiaNew < prevDiariaGuia && Number.isFinite(diariaSistema) && diariaSistema > 0) {
        const maxPct = Math.max(0, Math.floor(100 * (1 - prevDiariaGuia / diariaSistema) * 10) / 10);
        const revertTo = String(maxPct);
        return { valid: false as const, rule: 2 as const, revertTo, message: `A Diária Guia do PAX ${pax} não pode ser menor que a do PAX ${prevPax}. Comissão máxima permitida: ${maxPct}%`, maxPct };
      }
      return { valid: true as const };
    },
    [form, getIndividualForPax]
  );

  const handleComissaoPctChange = useCallback(
    (index: number, value: string, variant: 'nacional' | 'bilingue') => {
      const onlyDigits = value.replace(/\D/g, '').slice(0, 2);
      const field = variant === 'nacional' ? 'comissaoPct' : 'comissaoPctBilingue';
      updatePasseioRow(index, field, onlyDigits);
      if (onlyDigits.length === 2) {
        const newPct = parseFloat(onlyDigits);
        if (Number.isFinite(newPct)) {
          const result = validateComissaoPax(index, newPct, variant);
          if (!result.valid) {
            updatePasseioRow(index, field, result.revertTo);
            showAlert(result.message, 'Aviso', 'Entendi');
          }
        }
      }
    },
    [updatePasseioRow, validateComissaoPax, showAlert]
  );

  const handleComissaoBlur = useCallback(
    (index: number, variant: 'nacional' | 'bilingue') => {
      const field = variant === 'nacional' ? 'comissaoPct' : 'comissaoPctBilingue';
      const row = form.passeioExclusivo[index];
      const valueStr = variant === 'nacional' ? row.comissaoPct : row.comissaoPctBilingue;
      if (valueStr.length === 2) return;
      const newPct = valueStr.trim() ? parseFloat(valueStr.replace(',', '.')) : NaN;
      if (!Number.isFinite(newPct)) return;
      const result = validateComissaoPax(index, newPct, variant);
      if (!result.valid) {
        updatePasseioRow(index, field, result.revertTo);
        showAlert(result.message, 'Aviso', 'Entendi');
      }
    },
    [form, validateComissaoPax, updatePasseioRow, showAlert]
  );

  const handleComissaoPctChangePax1To4 = useCallback(
    (value: string, variant: 'nacional' | 'bilingue') => {
      const onlyDigits = value.replace(/\D/g, '').slice(0, 2);
      updatePasseioRowsPax1To4(variant === 'nacional' ? 'comissaoPct' : 'comissaoPctBilingue', onlyDigits);
      if (onlyDigits.length === 2) {
        const newPct = parseFloat(onlyDigits);
        if (Number.isFinite(newPct)) {
          const result = validateComissaoPax(0, newPct, variant);
          if (!result.valid) {
            updatePasseioRowsPax1To4(variant === 'nacional' ? 'comissaoPct' : 'comissaoPctBilingue', result.revertTo);
            showAlert(result.message, 'Aviso', 'Entendi');
          }
        }
      }
    },
    [updatePasseioRowsPax1To4, validateComissaoPax, showAlert]
  );

  const handleComissaoBlurPax1To4 = useCallback(
    (variant: 'nacional' | 'bilingue') => {
      const field = variant === 'nacional' ? 'comissaoPct' : 'comissaoPctBilingue';
      const row = form.passeioExclusivo[0];
      const valueStr = variant === 'nacional' ? row.comissaoPct : row.comissaoPctBilingue;
      if (valueStr.length === 2) return;
      const newPct = valueStr.trim() ? parseFloat(valueStr.replace(',', '.')) : NaN;
      if (!Number.isFinite(newPct)) return;
      const result = validateComissaoPax(0, newPct, variant);
      if (!result.valid) {
        updatePasseioRowsPax1To4(field, result.revertTo);
        showAlert(result.message, 'Aviso', 'Entendi');
      }
    },
    [form, validateComissaoPax, updatePasseioRowsPax1To4, showAlert]
  );

  const parseNum = (s: string): number | undefined => {
    const v = s.trim().replace(',', '.');
    if (!v) return undefined;
    const n = parseFloat(v);
    return isNaN(n) ? undefined : n;
  };

  const updatePasseioComissaoPctPax1to4 = useCallback((t: Tarifario, value: string) => {
    const pct = parseNum(value);
    const arr = [...(t.passeioExclusivo ?? [])];
    while (arr.length < 12) arr.push({});
    for (let i = 0; i < 4; i++) {
      const prev = arr[i] ?? {};
      if (pct != null && Number.isFinite(pct)) {
        arr[i] = { ...prev, comissaoPct: pct };
      } else {
        const { comissaoPct: _c, ...rest } = prev;
        arr[i] = rest;
      }
    }
    updateTarifario(t.id, { passeioExclusivo: arr });
    refresh();
  }, []);

  const handleSave = async () => {
    const n = form.nome.trim();
    if (!n) {
      setError('Informe o nome do grupo de tarifas.');
      return;
    }
    const pisoDiariaGuia = Math.max(PISO_DIARIA_GUIA_MINIMO, parseNum(globalConfig.pisoDiariaGuia ?? '') ?? PISO_DIARIA_GUIA_MINIMO);
    const diariaMinimaNum = form.diariaMinima.trim() ? parseFloat(form.diariaMinima.replace(',', '.')) : undefined;
    if (diariaMinimaNum != null && Number.isFinite(diariaMinimaNum) && diariaMinimaNum < pisoDiariaGuia) {
      setError(`Diária Mínima (Até 4 PAX) não pode ser menor que o PISO Diária GUIA (R$ ${pisoDiariaGuia}).`);
      return;
    }
    const excursaoIndividualBrl = parseNum(form.excursaoIndividual.brl);
    const individualExclusivoPax4 = (diariaMinimaNum != null && Number.isFinite(diariaMinimaNum)) ? diariaMinimaNum / 4 : NaN;
    const maxIndividualPax5 = Number.isFinite(individualExclusivoPax4) ? individualExclusivoPax4 - 8 : Infinity;
    if (excursaoIndividualBrl != null && Number.isFinite(excursaoIndividualBrl) && excursaoIndividualBrl >= maxIndividualPax5) {
      setError(`Individual PAX 5 Desconto Grupo deve ser menor que ${Math.round(maxIndividualPax5)}.`);
      return;
    }
    const transportInds = computeTransportIndividuals(form);
    if (Number.isFinite(transportInds[1].num) && Number.isFinite(transportInds[2].num) && transportInds[1].num > transportInds[2].num) {
      showAlert('O valor individual do Veículo 4x4 ou SUV (7 lugares) não pode ser maior que o do Veículo 4x4 ou SUV (5 lugares).', 'Aviso', 'Entendi');
      return;
    }
    if (Number.isFinite(transportInds[5].num) && Number.isFinite(transportInds[6].num) && transportInds[5].num < transportInds[6].num) {
      showAlert('Erro: o valor da Van 16 deve ser maior que o da Van 22, pois o custo é dividido entre menos pessoas.', 'Erro', 'Entendi');
      return;
    }
    const global = formToGlobalConfig(globalConfig);
    const taxaPct = parseNum(form.taxaGeralAgencia);
    const passeioExclusivoBase = form.passeioExclusivo.map((r, i) => {
      const pax = i + 1;
      let brl: number | undefined;
      if (pax <= 4 && diariaMinimaNum != null && Number.isFinite(diariaMinimaNum)) {
        brl = diariaMinimaNum / pax;
      } else if (pax >= 5 && excursaoIndividualBrl != null && Number.isFinite(excursaoIndividualBrl)) {
        brl = excursaoIndividualBrl;
      } else {
        brl = r.brl.trim() ? parseFloat(r.brl.replace(',', '.')) : undefined;
      }
      const comissaoPct = r.comissaoPct.trim() ? r.comissaoPct : (taxaPct != null ? String(taxaPct) : r.comissaoPct);
      const comissaoPctBilingue = r.comissaoPctBilingue.trim() ? r.comissaoPctBilingue : (taxaPct != null ? String(taxaPct) : r.comissaoPctBilingue);
      const rowWithProps = {
        ...r,
        brl: brl != null ? String(brl) : r.brl,
        comissaoPct,
        comissaoPctBilingue,
      };
      return passeioRowToItem(rowWithProps, i);
    });
    const payload: Omit<Tarifario, 'id'> = {
      nome: n,
      descricao: form.descricao.trim() || undefined,
      taxaGeralAgencia: global.taxaGeralAgencia,
      taxaMinimaAgencia: global.taxaMinimaAgencia,
      taxaMaximaAgencia: global.taxaMaximaAgencia,
      excursaoIndividual: rowToItem(form.excursaoIndividual),
      excursaoIndividualTranslado: transportRowToItem(form.excursaoIndividualTranslado),
      veiculo4x4_6: transportRowToItem(form.veiculo4x4_6),
      veiculo4x4_4: transportRowToItem(form.veiculo4x4_4),
      veiculoPasseio: transportRowToItem(form.veiculoPasseio),
      van15: transportRowToItem(form.van15),
      van20: transportRowToItem(form.van20),
      taxi: transportRowToItem(form.taxi ?? emptyTransportRow()),
      deslocamentoMoto: transportRowToItem(form.deslocamentoMoto),
      deslocamentoCarro: rowToItem({ brl: '', usd: '' }),
      passeioExclusivo: passeioExclusivoBase,
      descontoIdoso: global.descontoIdoso,
      descontoAdolescenteCrianca: global.descontoCrianca,
      descontoCrianca: global.descontoCrianca,
      descontoAdolescente: global.descontoAdolescente,
      descontoBebe: global.descontoBebe,
      aditivoGuiaBilingue: parseNum(form.aditivoGuiaBilingue),
    };
    if (editingId) {
      updateTarifario(editingId, payload);
    } else {
      addTarifario(payload);
    }
    refresh();
    closeModal();
  };

  const handleRemove = async (id: string) => {
    const confirmed = await showConfirm('Excluir este tarifário?', {
      title: 'Confirmar exclusão',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
    });
    if (!confirmed) return;
    const result = removeTarifario(id);
    if (result.ok) {
      refresh();
      if (editingId === id) closeModal();
    } else {
      alert(result.error ?? 'Não foi possível excluir.');
    }
  };

  const handleCopy = async (t: Tarifario) => {
    const confirmed = await showConfirm(`Duplicar o tarifário "${t.nome}" para nova edição?`, {
      title: 'Confirmar duplicação',
      confirmText: 'Duplicar',
      cancelText: 'Cancelar',
    });
    if (!confirmed) return;
    const { id: _id, ...rest } = t;
    const novo = addTarifario({ ...rest, nome: `Cópia de ${t.nome}` });
    refresh();
    openEdit(novo);
  };

  const updateInlineField = useCallback(
    (t: Tarifario, field: 'excursaoIndividualBrl' | 'diariaMinimaPax4' | 'excursaoIndividualTransladoBrl' | 'veiculo4x4_4Brl' | 'veiculo4x4_6Brl' | 'veiculoPasseioBrl' | 'taxiBrl' | 'aditivoGuiaBilingue', value: string) => {
      const num = parseNum(value);
      if (field === 'excursaoIndividualBrl') {
        updateTarifario(t.id, {
          excursaoIndividual: { ...t.excursaoIndividual, brl: num },
        });
      } else if (field === 'diariaMinimaPax4') {
        const diariaMinima = num;
        const arr = [...(t.passeioExclusivo ?? Array.from({ length: 12 }, () => ({})))];
        while (arr.length < 12) arr.push({});
        if (diariaMinima != null && Number.isFinite(diariaMinima)) {
          for (let i = 0; i < 4; i++) {
            arr[i] = { ...arr[i], brl: diariaMinima / (i + 1) };
          }
        }
        updateTarifario(t.id, { passeioExclusivo: arr });
      } else if (field === 'excursaoIndividualTransladoBrl') {
        updateTarifario(t.id, {
          excursaoIndividualTranslado: { ...(t.excursaoIndividualTranslado ?? {}), brl: num },
        });
      } else if (field === 'veiculo4x4_4Brl') {
        updateTarifario(t.id, {
          veiculo4x4_4: { ...t.veiculo4x4_4, brl: num },
        });
      } else if (field === 'veiculo4x4_6Brl') {
        updateTarifario(t.id, {
          veiculo4x4_6: { ...t.veiculo4x4_6, brl: num },
        });
      } else if (field === 'veiculoPasseioBrl') {
        updateTarifario(t.id, {
          veiculoPasseio: { ...t.veiculoPasseio, brl: num },
        });
      } else if (field === 'taxiBrl') {
        updateTarifario(t.id, {
          taxi: { ...t.taxi, brl: num },
        });
      } else if (field === 'aditivoGuiaBilingue') {
        updateTarifario(t.id, {
          aditivoGuiaBilingue: num ?? undefined,
        });
      }
      refresh();
    },
    []
  );

  const inputClass =
    'w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm';

  const IconPencil = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
  );
  const IconTrash = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
  );
  const IconCopy = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
  );
  const rowBgClass = (i: number) => {
    const shades = ['bg-white', 'bg-gray-50', 'bg-orange-50'] as const;
    return shades[i % shades.length];
  };

  return (
    <AdminLayout>
      <section className="w-full bg-white min-h-screen max-w-full">
        <div className="sticky top-0 z-20 px-4 sm:px-6 py-5 border-b border-gray-200/80 w-full bg-white/95 backdrop-blur-sm shadow-sm">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Tarifários</h1>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={openNew}
                className="px-5 py-2.5 bg-orange-500 text-white rounded-xl font-semibold text-base shadow-sm hover:bg-orange-600 transition-colors"
              >
                Novo Tarifário Passeio
              </button>
              <button
                type="button"
                disabled
                className="px-5 py-2.5 border-2 border-gray-200 text-gray-400 rounded-xl font-semibold text-base cursor-not-allowed bg-gray-50"
                title="Em breve"
              >
                Tarifário Terapia
              </button>
            </div>
          </div>
        </div>

        {/* Configurações globais / Premiações — abas no estilo da página Experiências */}
        <div className="px-4 sm:px-6 py-5 border-b border-gray-100 bg-gradient-to-b from-slate-50/90 to-gray-50/50 w-full space-y-4">
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2 flex-wrap">
            {[
              { id: 'configuracoes' as const, label: 'Configurações globais' },
              { id: 'premiacoes' as const, label: 'Premiações' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setGlobalSectionTab(tab.id)}
                className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${globalSectionTab === tab.id ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {globalSectionTab === 'configuracoes' && (
            <>
              <p className="text-sm text-blue-700/90 mb-1">Taxas e descontos aplicados globalmente. Ficam no topo das tarifas.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Taxa agência (%)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={globalConfig.taxaGeralAgencia}
                    onChange={(e) => updateGlobalConfig('taxaGeralAgencia', e.target.value)}
                    placeholder="Ex: 8"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Taxa mínima (%)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={globalConfig.taxaMinimaAgencia}
                    onChange={(e) => updateGlobalConfig('taxaMinimaAgencia', e.target.value)}
                    placeholder="Ex: 5"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Taxa máxima (%)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={globalConfig.taxaMaximaAgencia}
                    onChange={(e) => updateGlobalConfig('taxaMaximaAgencia', e.target.value)}
                    placeholder="%"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Desconto idoso (%)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={globalConfig.descontoIdoso}
                    onChange={(e) => updateGlobalConfig('descontoIdoso', e.target.value)}
                    placeholder="%"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Desconto Criança (%)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={globalConfig.descontoCrianca ?? ''}
                    onChange={(e) => updateGlobalConfig('descontoCrianca', e.target.value)}
                    placeholder="50"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Desconto Adolescente (%)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={globalConfig.descontoAdolescente ?? ''}
                    onChange={(e) => updateGlobalConfig('descontoAdolescente', e.target.value)}
                    placeholder="0"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Desconto Guiagem Compartilhada (%)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={globalConfig.descontoGuia}
                    onChange={(e) => updateGlobalConfig('descontoGuia', e.target.value)}
                    placeholder="Ex: 30"
                    className={inputClass}
                    title="Desconto aplicado a passeios compartilhados no checkout do cliente"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Desconto Veículo Compartilhado (%)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={globalConfig.descontoVeiculo}
                    onChange={(e) => updateGlobalConfig('descontoVeiculo', e.target.value)}
                    placeholder="Ex: 10"
                    className={inputClass}
                    title="Desconto aplicado em veículos"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Desconto bebê (%)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={globalConfig.descontoBebe ?? ''}
                    onChange={(e) => updateGlobalConfig('descontoBebe', e.target.value)}
                    placeholder="100"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Taxa administrativa (%)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={globalConfig.taxaServicoIngresso ?? ''}
                    onChange={(e) => updateGlobalConfig('taxaServicoIngresso', e.target.value)}
                    placeholder="Ex: 10"
                    className={inputClass}
                    title="Taxa aplicada sobre ingressos/entradas incluídas no checkout"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PISO Diária GUIA (R$)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={globalConfig.pisoDiariaGuia ?? ''}
                    onChange={(e) => updateGlobalConfig('pisoDiariaGuia', e.target.value)}
                    placeholder="250"
                    className={inputClass}
                    title="Mínimo que a Diária Mínima (Até 4 PAX) pode ter. Valor mínimo do piso: R$ 250."
                  />
                  <p className="text-xs text-gray-500 mt-1">Mínimo: R$ 250. Diária Mínima não pode ser menor que este valor.</p>
                </div>
              </div>
            </>
          )}

          {globalSectionTab === 'premiacoes' && (
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-xs text-gray-500 mt-1 mb-3">Mensal e anual para 1º, 2º e 3º lugar.</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Mensal</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Primeiro</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={globalConfig.premioMensalPrimeiro}
                        onChange={(e) => updateGlobalConfig('premioMensalPrimeiro', e.target.value)}
                        placeholder=""
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Segundo</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={globalConfig.premioMensalSegundo}
                        onChange={(e) => updateGlobalConfig('premioMensalSegundo', e.target.value)}
                        placeholder=""
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Terceiro</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={globalConfig.premioMensalTerceiro}
                        onChange={(e) => updateGlobalConfig('premioMensalTerceiro', e.target.value)}
                        placeholder=""
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Anual</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Primeiro</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={globalConfig.premioAnualPrimeiro}
                        onChange={(e) => updateGlobalConfig('premioAnualPrimeiro', e.target.value)}
                        placeholder=""
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Segundo</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={globalConfig.premioAnualSegundo}
                        onChange={(e) => updateGlobalConfig('premioAnualSegundo', e.target.value)}
                        placeholder=""
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Terceiro</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={globalConfig.premioAnualTerceiro}
                        onChange={(e) => updateGlobalConfig('premioAnualTerceiro', e.target.value)}
                        placeholder=""
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-4 sm:px-6 py-6 w-full max-w-full overflow-x-auto">
          <div className="bg-white rounded-2xl border border-gray-200/80 shadow-md overflow-hidden ring-1 ring-gray-100">
            {list.length === 0 ? (
              <div className="p-10 text-center text-gray-600 text-base">
                Nenhum tarifário cadastrado. Adicione um para usar nas experiências (cidades de embarque).
              </div>
            ) : (
              <table className="min-w-full text-base border-collapse">
                <thead>
                  <tr className="bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-sm">
                    <th className="px-4 py-3.5 text-left text-base font-bold align-bottom min-w-[10rem]">Nome</th>
                    <th className="px-3 py-3.5 text-left align-bottom min-w-[6.5rem]">
                      <span className="block text-[0.65rem] font-semibold uppercase tracking-wider text-white/85">BRL</span>
                      <span className="block text-sm font-bold leading-snug">Diária Mínima (Até 4 PAX)</span>
                    </th>
                    <th className="px-3 py-3.5 text-left align-bottom min-w-[6.5rem]">
                      <span className="block text-[0.65rem] font-semibold uppercase tracking-wider text-white/85">BRL</span>
                      <span className="block text-sm font-bold leading-snug">Individual até PAX 4</span>
                    </th>
                    <th className="px-3 py-3.5 text-left align-bottom min-w-[6.5rem]">
                      <span className="block text-[0.65rem] font-semibold uppercase tracking-wider text-white/85">BRL</span>
                      <span className="block text-sm font-bold leading-snug">Taxa plataforma (PAX 4)</span>
                    </th>
                    <th className="px-3 py-3.5 text-left align-bottom min-w-[6.5rem]">
                      <span className="block text-[0.65rem] font-semibold uppercase tracking-wider text-white/85">BRL</span>
                      <span className="block text-sm font-bold leading-snug">Diária guia exclusiva (PAX 4)</span>
                    </th>
                    <th className="px-3 py-3.5 text-left align-bottom min-w-[5.5rem]">
                      <span className="block text-[0.65rem] font-semibold uppercase tracking-wider text-white/85">BRL</span>
                      <span className="block text-sm font-bold leading-snug">Valor Táxi</span>
                    </th>
                    <th className="px-3 py-3.5 text-left align-bottom min-w-[4.5rem] text-sm font-bold leading-tight">
                      Taxa nacional
                      <span className="block text-[0.65rem] font-normal text-white/90">PAX 1–4 (%)</span>
                    </th>
                    <th className="px-3 py-3.5 text-left align-bottom min-w-[6.5rem]">
                      <span className="block text-[0.65rem] font-semibold uppercase tracking-wider text-white/85">BRL</span>
                      <span className="block text-sm font-bold leading-snug">Individual Excursão Desconto</span>
                    </th>
                    <th className="px-3 py-3.5 text-left align-bottom min-w-[6.5rem]">
                      <span className="block text-[0.65rem] font-semibold uppercase tracking-wider text-white/85">BRL</span>
                      <span className="block text-sm font-bold leading-snug">Individual Transporte Excursão</span>
                    </th>
                    <th className="px-3 py-3.5 text-left align-bottom min-w-[5.5rem]">
                      <span className="block text-[0.65rem] font-semibold uppercase tracking-wider text-white/85">BRL</span>
                      <span className="block text-sm font-bold leading-snug">SUV</span>
                    </th>
                    <th className="px-3 py-3.5 text-left align-bottom min-w-[6rem]">
                      <span className="block text-[0.65rem] font-semibold uppercase tracking-wider text-white/85">BRL</span>
                      <span className="block text-sm font-bold leading-snug">Compacto</span>
                    </th>
                    <th className="px-3 py-3.5 text-left align-bottom min-w-[5rem] text-sm font-bold">Aditivo Guia (%)</th>
                    <th className="px-3 py-3.5 text-left align-bottom min-w-[6.75rem]">
                      <span className="block text-[0.65rem] font-semibold uppercase tracking-wider text-white/85">BRL</span>
                      <span className="block text-sm font-bold leading-snug">Diária guia exclusiva PAX 4 (bilíngue)</span>
                    </th>
                    <th className="px-4 py-3.5 text-right text-sm font-bold w-36">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((t, idx) => {
                    const rowPax4 = t.passeioExclusivo?.[3];
                    const individualPax4 = rowPax4?.brl;
                    const diariaMinimaPax4 = individualPax4 != null ? individualPax4 * 4 : undefined;
                    const individualAtePax4Display =
                      individualPax4 != null && Number.isFinite(individualPax4) ? String(Math.round(individualPax4)) : '—';
                    const pax4Derived = computeDashboardPax4ListDerived(t, globalConfig.taxaGeralAgencia);
                    const taxaPctInputValue =
                      pax4Derived.taxaPctNacional != null ? String(Math.round(pax4Derived.taxaPctNacional)) : '';
                    const cellInput =
                      'min-w-[5.25rem] w-full max-w-[6.75rem] py-2.5 px-3 rounded-lg border border-gray-200 bg-white text-base tabular-nums shadow-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400';
                    const cellReadonly =
                      'min-w-[5.25rem] max-w-[6.75rem] py-2.5 px-3 rounded-lg border border-gray-200 bg-gray-100 text-base font-medium text-gray-800 tabular-nums text-right';
                    return (
                      <tr key={t.id} className={`${rowBgClass(idx)} border-b border-gray-100 last:border-b-0 hover:bg-orange-50/40 transition-colors`}>
                        <td className="px-4 py-3.5 align-middle">
                          <span className="text-lg font-semibold text-gray-900 tracking-tight">{t.nome}</span>
                          {t.descricao && (
                            <span className="text-gray-500 text-sm ml-1 block mt-0.5">— {t.descricao}</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 align-middle">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={diariaMinimaPax4 != null ? String(Math.round(diariaMinimaPax4)) : ''}
                            onChange={(e) => updateInlineField(t, 'diariaMinimaPax4', e.target.value)}
                            onBlur={(e) => updateInlineField(t, 'diariaMinimaPax4', e.target.value)}
                            className={cellInput}
                            placeholder=""
                          />
                        </td>
                        <td className="px-3 py-2.5 align-middle">
                          <div
                            className="min-w-[5.25rem] max-w-[6.75rem] py-2.5 px-3 rounded-lg border border-gray-200 bg-gray-100 text-base font-medium text-gray-800 tabular-nums text-right"
                            title="Calculado: Diária Mínima ÷ 4 (editar na coluna Diária Mínima ou no modal)"
                          >
                            {individualAtePax4Display}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 align-middle">
                          <div
                            className={`${cellReadonly} !text-blue-600 font-semibold`}
                            title="Diária sistema PAX 4 × taxa nacional (%)"
                          >
                            {formatBrl(pax4Derived.taxaPlataformaBrl)}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 align-middle">
                          <div
                            className={cellReadonly}
                            title="Diária sistema PAX 4 − taxa plataforma"
                          >
                            {formatBrl(pax4Derived.diariaGuiaNacionalPax4)}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 align-middle">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={t.taxi?.brl != null ? String(t.taxi.brl) : ''}
                            onChange={(e) => updateInlineField(t, 'taxiBrl', e.target.value)}
                            onBlur={(e) => updateInlineField(t, 'taxiBrl', e.target.value)}
                            className={cellInput}
                            placeholder=""
                          />
                        </td>
                        <td className="px-3 py-2.5 align-middle">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={taxaPctInputValue}
                            onChange={(e) => updatePasseioComissaoPctPax1to4(t, e.target.value)}
                            onBlur={(e) => updatePasseioComissaoPctPax1to4(t, e.target.value)}
                            className={`${cellInput} max-w-[4.5rem]`}
                            placeholder="%"
                            title="Taxa plataforma PAX 1–4 (nacional). Vazio = usar taxa global / tarifário."
                          />
                        </td>
                        <td className="px-3 py-2.5 align-middle">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={t.excursaoIndividual?.brl ?? ''}
                            onChange={(e) => updateInlineField(t, 'excursaoIndividualBrl', e.target.value)}
                            onBlur={(e) => updateInlineField(t, 'excursaoIndividualBrl', e.target.value)}
                            className={cellInput}
                            placeholder=""
                          />
                        </td>
                        <td className="px-3 py-2.5 align-middle">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={t.excursaoIndividualTranslado?.brl ?? ''}
                            onChange={(e) => updateInlineField(t, 'excursaoIndividualTransladoBrl', e.target.value)}
                            onBlur={(e) => updateInlineField(t, 'excursaoIndividualTransladoBrl', e.target.value)}
                            className={cellInput}
                            placeholder=""
                          />
                        </td>
                        <td className="px-3 py-2.5 align-middle">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={t.veiculo4x4_4?.brl ?? ''}
                            onChange={(e) => updateInlineField(t, 'veiculo4x4_4Brl', e.target.value)}
                            onBlur={(e) => updateInlineField(t, 'veiculo4x4_4Brl', e.target.value)}
                            className={cellInput}
                            placeholder=""
                          />
                        </td>
                        <td className="px-3 py-2.5 align-middle">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={t.veiculoPasseio?.brl ?? ''}
                            onChange={(e) => updateInlineField(t, 'veiculoPasseioBrl', e.target.value)}
                            onBlur={(e) => updateInlineField(t, 'veiculoPasseioBrl', e.target.value)}
                            className={cellInput}
                            placeholder=""
                          />
                        </td>
                        <td className="px-3 py-2.5 align-middle">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={t.aditivoGuiaBilingue != null ? String(t.aditivoGuiaBilingue) : ''}
                            onChange={(e) => updateInlineField(t, 'aditivoGuiaBilingue', e.target.value)}
                            onBlur={(e) => updateInlineField(t, 'aditivoGuiaBilingue', e.target.value)}
                            className={`${cellInput} max-w-[4.5rem]`}
                            placeholder="%"
                          />
                        </td>
                        <td className="px-3 py-2.5 align-middle">
                          <div
                            className={cellReadonly}
                            title="Individual PAX 4 bilíngue × 4, menos taxa bilíngue (aba Bilíngue)"
                          >
                            {formatBrl(pax4Derived.diariaGuiaBilinguePax4)}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-right align-middle">
                          <div className="flex items-center justify-end gap-0.5">
                            <button
                              type="button"
                              onClick={() => openEdit(t)}
                              className="p-2 text-gray-600 hover:text-orange-600 rounded-xl hover:bg-orange-50 shrink-0 transition-colors"
                              title="Editar"
                            >
                              <IconPencil />
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleCopy(t)}
                              className="p-2 text-gray-600 hover:text-orange-600 rounded-xl hover:bg-orange-50 shrink-0 transition-colors"
                              title="Duplicar tarifa"
                            >
                              <IconCopy />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemove(t.id)}
                              className="p-2 text-gray-600 hover:text-red-600 rounded-xl hover:bg-red-50 shrink-0 transition-colors"
                              title="Excluir"
                            >
                              <IconTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeModal}>
            <div
              className="bg-white rounded-xl shadow-xl w-full max-w-[min(112rem,95vw)] max-h-[95vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingId ? 'Editar Tarifário Passeio' : 'Novo Tarifário Passeio'}
                </h2>
              </div>
              <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 w-full min-w-0">
                {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

                {/* Container tarifário — sempre visível no topo */}
                <section className="bg-gray-50 rounded-xl p-6 border border-gray-200 w-full min-w-0 overflow-hidden mb-6">
                  <div className="flex flex-wrap items-center gap-3 w-full min-w-0">
                    <h3 className="text-lg font-semibold text-gray-800 shrink-0">Nome do Grupo de Tarifas</h3>
                    <input
                      type="text"
                      value={form.nome}
                      onChange={(e) => updateForm('nome', e.target.value)}
                      className="flex-1 min-w-[200px] max-w-md py-2 px-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm"
                      placeholder="Ex: Trilha Difícil"
                    />
                  </div>
                </section>

                {/* Abas */}
                <div className="flex gap-2 mb-4 border-b border-gray-200 pb-2">
                  {[
                    { id: 'tarifario' as const, label: 'Tarifário', flag: '' },
                    { id: 'transporte' as const, label: 'Transporte', flag: '' },
                    { id: 'nacional' as const, label: 'Nacional', flag: '🇧🇷' },
                    { id: 'bilingue' as const, label: 'Bilingue', flag: '🇺🇸' },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setFormTab(tab.id)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${formTab === tab.id ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    >
                      {tab.flag ? `${tab.flag} ${tab.label}` : tab.label}
                    </button>
                  ))}
                </div>

                {/* Conteúdo das abas */}
                {formTab === 'tarifario' && (
                  <section className="bg-gray-50 rounded-xl p-6 border border-gray-200 w-full min-w-0 overflow-x-auto">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 w-full">Tarifário</h3>
                    <div className="grid grid-cols-[minmax(26rem,2.5fr)_6rem_1fr] gap-x-2 gap-y-4 w-full min-w-0 max-w-4xl items-center">
                      <div className="py-2 px-3 font-semibold text-gray-800 text-sm" />
                      <div className="py-2 px-3 font-semibold text-gray-800 text-sm text-center" />
                      <div />
                      <label className="text-sm font-medium text-gray-700 truncate min-w-0 py-2 px-3">Diária Mínima (Até 4 PAX)</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder=""
                        value={form.diariaMinima}
                        onChange={(e) => { updateForm('diariaMinima', e.target.value); setError(''); }}
                        onBlur={handleDiariaMinimaBlur}
                        className="w-full min-w-0 py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm"
                        title="Não pode ser menor que o PISO Diária GUIA"
                      />
                      <span className="text-xs text-blue-600 py-2 px-2 self-center whitespace-nowrap min-w-max">Diária mínima exclusiva para até 4 pessoas</span>
                      <label className="text-sm font-medium text-gray-700 truncate min-w-0 py-2 px-3">Individual (PAX 4) Excursão Unitário</label>
                      <div className="py-2 px-3 text-sm text-gray-600 bg-gray-100 rounded border border-gray-200 font-medium tabular-nums">
                        {(() => {
                          const n = form.diariaMinima.trim() ? parseFloat(form.diariaMinima.replace(',', '.')) : NaN;
                          return Number.isFinite(n) ? String(Math.round(n / 4)) : '—';
                        })()}
                      </div>
                      <span className="text-xs text-gray-500 py-2 px-2 self-center whitespace-nowrap min-w-max">Calculado: Diária Mínima ÷ 4 (somente leitura)</span>
                      <label className="text-sm font-medium text-gray-700 truncate min-w-0 py-2 px-3">Individual PAX 5 Desconto Grupo</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder=""
                        value={form.excursaoIndividual.brl}
                        onChange={(e) => {
                          updateRow('excursaoIndividual', 'brl', e.target.value);
                          setError('');
                        }}
                        onBlur={handleExcursaoIndividualBlur}
                        className={`w-full min-w-0 py-2 px-3 rounded border text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent ${
                          (() => {
                            const n = form.diariaMinima.trim() ? parseFloat(form.diariaMinima.replace(',', '.')) : NaN;
                            const max = Number.isFinite(n) ? n / 4 - 8 : Infinity;
                            const val = form.excursaoIndividual.brl.trim() ? parseFloat(form.excursaoIndividual.brl.replace(',', '.')) : NaN;
                            return Number.isFinite(val) && val >= max ? 'border-red-500 bg-red-50' : 'border-gray-300';
                          })()
                        }`}
                      />
                      <span className={`text-xs py-2 px-2 self-center whitespace-nowrap min-w-max ${
                        (() => {
                          const n = form.diariaMinima.trim() ? parseFloat(form.diariaMinima.replace(',', '.')) : NaN;
                          const max = Number.isFinite(n) ? n / 4 - 8 : Infinity;
                          const val = form.excursaoIndividual.brl.trim() ? parseFloat(form.excursaoIndividual.brl.replace(',', '.')) : NaN;
                          return Number.isFinite(val) && val >= max ? 'text-red-600 font-semibold' : 'text-amber-700';
                        })()
                      }`}>
                        {(() => {
                          const n = form.diariaMinima.trim() ? parseFloat(form.diariaMinima.replace(',', '.')) : NaN;
                          const max = Number.isFinite(n) ? Math.round(n / 4 - 8) : null;
                          return max != null
                            ? `Deve ser menor que ${max}. Valor individual de 5 a 12 pessoas escolhendo compartilhado.`
                            : 'Valor individual de 5 a 12 pessoas escolhendo compartilhado.';
                        })()}
                      </span>
                    </div>
                  </section>
                )}

                {formTab === 'transporte' && (
                  <section className="bg-gray-50 rounded-xl p-6 border border-gray-200 w-full min-w-0 overflow-x-auto">
                    <div className="flex flex-wrap items-center gap-3 mb-4 w-full">
                      <h3 className="text-lg font-semibold text-gray-800 shrink-0">Transporte</h3>
                      <p className="text-sm text-gray-600 flex-1 min-w-[12rem] leading-snug">
                        Os preços são calculados em cima do valor Individual com Translado.
                      </p>
                      <button
                        type="button"
                        onClick={handleEqualizarPrecosTransporte}
                        title="Taxi = Individual×2 | 5 lug = Individual×4 − Individual÷2 | Compacto = Individual×3 | 7 lug = Individual×5 | Moto = Individual"
                        className="shrink-0 px-4 py-2 rounded-lg text-sm font-semibold bg-slate-700 text-white hover:bg-slate-800 shadow-sm transition-colors"
                      >
                        Equalizar preços
                      </button>
                    </div>
                    <div className={`grid ${TRANSPORT_GRID} w-full min-w-0 items-center`}>
                      <TransportSectionHeader />
                      {(() => {
                        const inds = computeTransportIndividuals(form);
                        return (
                          <>
                            <TarifaTransportRow
                              label="Individual com Translado"
                              brl={form.excursaoIndividualTranslado.brl}
                              comissaoPct={form.excursaoIndividualTranslado.comissaoPct}
                              individual={inds[0].value}
                              individualNum={inds[0].num}
                              onBrlChange={(v) => updateRow('excursaoIndividualTranslado', 'brl', v)}
                              onComissaoPctChange={(v) => updateRow('excursaoIndividualTranslado', 'comissaoPct', v)}
                              highlightPlataformaBrl
                            />
                            <TarifaTransportRow
                              label="Taxi"
                              brl={form.taxi?.brl ?? ''}
                              comissaoPct={form.taxi?.comissaoPct ?? ''}
                              individual={inds[1].value}
                              individualNum={inds[1].num}
                              onBrlChange={(v) => updateRow('taxi', 'brl', v)}
                              onComissaoPctChange={(v) => updateRow('taxi', 'comissaoPct', v)}
                            />
                            <TarifaTransportRow
                              label="Veículo 4x4 ou SUV (5 lugares)"
                              brl={form.veiculo4x4_4.brl}
                              comissaoPct={form.veiculo4x4_4.comissaoPct}
                              individual={inds[2].value}
                              individualNum={inds[2].num}
                              onBrlChange={(v) => updateRow('veiculo4x4_4', 'brl', v)}
                              onComissaoPctChange={(v) => updateRow('veiculo4x4_4', 'comissaoPct', v)}
                            />
                            <TarifaTransportRow
                              label="Compacto"
                              brl={form.veiculoPasseio.brl}
                              comissaoPct={form.veiculoPasseio.comissaoPct}
                              individual={inds[3].value}
                              individualNum={inds[3].num}
                              onBrlChange={(v) => updateRow('veiculoPasseio', 'brl', v)}
                              onComissaoPctChange={(v) => updateRow('veiculoPasseio', 'comissaoPct', v)}
                            />
                            <TarifaTransportRow
                              label="Veículo 4x4 ou SUV (7 lugares)"
                              brl={form.veiculo4x4_6.brl}
                              comissaoPct={form.veiculo4x4_6.comissaoPct}
                              individual={inds[4].value}
                              individualNum={inds[4].num}
                              onBrlChange={(v) => updateRow('veiculo4x4_6', 'brl', v)}
                              onComissaoPctChange={(v) => updateRow('veiculo4x4_6', 'comissaoPct', v)}
                            />
                            <TarifaTransportRow
                              label="Van 16 lugares"
                              brl={form.van15.brl}
                              comissaoPct={form.van15.comissaoPct}
                              individual={inds[5].value}
                              individualNum={inds[5].num}
                              onBrlChange={(v) => updateRow('van15', 'brl', v)}
                              onComissaoPctChange={(v) => updateRow('van15', 'comissaoPct', v)}
                            />
                            <TarifaTransportRow
                              label="Van 22 Lugares"
                              brl={form.van20.brl}
                              comissaoPct={form.van20.comissaoPct}
                              individual={inds[6].value}
                              individualNum={inds[6].num}
                              onBrlChange={(v) => updateRow('van20', 'brl', v)}
                              onComissaoPctChange={(v) => updateRow('van20', 'comissaoPct', v)}
                            />
                            <TarifaTransportRow
                              label="Deslocamento do guia em motocicleta"
                              brl={form.deslocamentoMoto.brl}
                              comissaoPct={form.deslocamentoMoto.comissaoPct}
                              individual={inds[7].value}
                              individualNum={inds[7].num}
                              onBrlChange={(v) => updateRow('deslocamentoMoto', 'brl', v)}
                              onComissaoPctChange={(v) => updateRow('deslocamentoMoto', 'comissaoPct', v)}
                            />
                          </>
                        );
                      })()}
                    </div>
                  </section>
                )}

                {formTab === 'nacional' && (
                  <section className="bg-gray-50 rounded-xl p-6 border border-gray-200 w-full min-w-0 overflow-hidden">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 w-full flex items-center gap-2">
                      <span className="text-2xl">🇧🇷</span> Nacional
                    </h3>
                    <div className="w-full min-w-0 overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr>
                            <th className="py-2 px-3 font-semibold text-sm text-gray-800 text-left">PAX</th>
                            <th className="py-2 px-3 font-semibold text-sm text-gray-800 text-center">
                              <span className="block text-xs text-gray-500 font-normal">BRL</span>
                              Individual
                            </th>
                            <th className="py-2 px-3 font-semibold text-sm text-gray-800 text-center" colSpan={2}>
                              <span className="block text-xs text-gray-500 font-normal">BRL</span>
                              Diária Plataforma Exclusivo | Excursão
                            </th>
                            <th className="py-2 px-3 font-semibold text-sm text-gray-800 text-center">
                              <span className="block text-xs text-gray-500 font-normal">%</span>
                              Taxa Plataforma
                            </th>
                            <th className="py-2 px-3 font-semibold text-sm text-gray-800 text-center" colSpan={2}>
                              <span className="block text-xs text-gray-500 font-normal">BRL</span>
                              Diária Guia Exclusiva | Excursão
                            </th>
                          </tr>
                          <tr className="text-xs font-medium text-gray-600">
                            <th className="py-1 px-3" />
                            <th className="py-1 px-3 text-center" />
                            <th className="py-1 px-3 text-center" colSpan={2} />
                            <th className="py-1 px-3 text-center" />
                            <th className="py-1 px-3 text-center" colSpan={2} />
                          </tr>
                        </thead>
                        <tbody>
                      {form.passeioExclusivo.map((row, i) => {
                        const pax = i + 1;
                        const diariaMinimaNum = form.diariaMinima.trim() ? parseFloat(form.diariaMinima.replace(',', '.')) : NaN;
                        const excursaoIndividualNum = form.excursaoIndividual.brl.trim() ? parseFloat(form.excursaoIndividual.brl.replace(',', '.')) : NaN;
                        const individualBrl = pax <= 4 && Number.isFinite(diariaMinimaNum)
                          ? diariaMinimaNum / pax
                          : pax >= 5 && Number.isFinite(excursaoIndividualNum)
                            ? excursaoIndividualNum
                            : NaN;
                        const individualPax4Brl = Number.isFinite(diariaMinimaNum) ? diariaMinimaNum / 4 : NaN;
                        const diariaSistemaBrl = Number.isFinite(individualBrl) ? individualBrl * pax : NaN;
                        const exclusivoPax5PlusBrl = pax >= 5 && Number.isFinite(individualPax4Brl) ? individualPax4Brl * pax : NaN;
                        const exclusivoPax5PlusBrlRight = pax >= 5 && Number.isFinite(individualBrl) ? individualBrl * pax : NaN;
                        const descontoGuiaPct = globalConfig.descontoGuia?.trim() ? parseFloat(globalConfig.descontoGuia.replace(',', '.')) : NaN;
                        const diariaSistemaParaDesconto = pax >= 5 && Number.isFinite(exclusivoPax5PlusBrl) ? exclusivoPax5PlusBrl : diariaSistemaBrl;
                        const descontoCompartilhadoBrlRaw = Number.isFinite(diariaSistemaParaDesconto) && Number.isFinite(descontoGuiaPct) ? diariaSistemaParaDesconto * (descontoGuiaPct / 100) : NaN;
                        const descontoCompartilhadoBrl = Number.isFinite(descontoCompartilhadoBrlRaw) ? Math.ceil(descontoCompartilhadoBrlRaw) : NaN;
                        const compartilhadoBrl = Number.isFinite(diariaSistemaParaDesconto) && Number.isFinite(descontoCompartilhadoBrl) ? diariaSistemaParaDesconto - descontoCompartilhadoBrl : NaN;
                        const pctComissao = row.comissaoPct.trim()
                          ? parseFloat(row.comissaoPct.replace(',', '.'))
                          : (form.taxaGeralAgencia.trim() ? parseFloat(form.taxaGeralAgencia.replace(',', '.')) : NaN);
                        const diariaSistemaParaComissao = pax >= 5 && Number.isFinite(exclusivoPax5PlusBrl) ? exclusivoPax5PlusBrl : diariaSistemaBrl;
                        const comissaoBrl = Number.isFinite(diariaSistemaParaComissao) && Number.isFinite(pctComissao) ? diariaSistemaParaComissao * (pctComissao / 100) : NaN;
                        const diariaGuiaBrl = Number.isFinite(diariaSistemaParaComissao) && Number.isFinite(comissaoBrl) ? diariaSistemaParaComissao - comissaoBrl : NaN;
                        const comissaoBrlRight = pax >= 5 && Number.isFinite(exclusivoPax5PlusBrlRight) && Number.isFinite(pctComissao) ? exclusivoPax5PlusBrlRight * (pctComissao / 100) : NaN;
                        const diariaGuiaBrlRight = pax >= 5 && Number.isFinite(exclusivoPax5PlusBrlRight) && Number.isFinite(comissaoBrlRight) ? exclusivoPax5PlusBrlRight - comissaoBrlRight : NaN;
                        const comissaoCompartilhadoBrl = Number.isFinite(compartilhadoBrl) && Number.isFinite(pctComissao) ? compartilhadoBrl * (pctComissao / 100) : NaN;
                        const diariaGuiaCompartilhadoBrl = Number.isFinite(compartilhadoBrl) && Number.isFinite(comissaoCompartilhadoBrl) ? compartilhadoBrl - comissaoCompartilhadoBrl : NaN;
                        return (
                          <tr key={i}>
                            <td className="py-2 px-3 text-sm font-medium text-gray-700">{pax}</td>
                            {pax <= 4 ? (
                              <td className={`py-2 px-3 text-sm text-center tabular-nums bg-gray-100 rounded border border-gray-200 ${pax === 4 ? 'text-blue-600 font-medium' : 'text-gray-700'}`}>
                                {formatBrl(individualBrl)}
                              </td>
                            ) : pax === 5 ? (
                              <td rowSpan={8} className="py-2 px-3 text-sm text-center tabular-nums bg-gray-100 rounded border border-gray-200 text-orange-600 font-medium align-middle">
                                {formatBrl(individualBrl)}
                              </td>
                            ) : null}
                            {pax >= 5 ? (
                              <>
                                <td className="py-2 px-3 text-sm text-gray-700 text-center tabular-nums rounded border border-blue-200 bg-blue-100">
                                  {formatBrl(exclusivoPax5PlusBrl)}
                                </td>
                                <td className="py-2 px-3 text-sm text-gray-700 text-center tabular-nums rounded border border-orange-200 bg-orange-100">
                                  {formatBrl(exclusivoPax5PlusBrlRight)}
                                </td>
                              </>
                            ) : pax === 1 ? (
                              <td rowSpan={4} colSpan={2} className="py-2 px-3 text-sm text-gray-700 text-center tabular-nums rounded border border-gray-200 bg-gray-100 align-middle">
                                {formatBrl(diariaSistemaBrl)}
                              </td>
                            ) : null}
                            {pax >= 5 ? (
                              <td className="p-0">
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={2}
                                  placeholder={form.taxaGeralAgencia || '%'}
                                  value={row.comissaoPct}
                                  onChange={(e) => handleComissaoPctChange(i, e.target.value, 'nacional')}
                                  onBlur={() => handleComissaoBlur(i, 'nacional')}
                                  className="w-full min-w-0 py-2 px-3 rounded border border-gray-300 bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm shadow-sm"
                                />
                              </td>
                            ) : pax === 1 ? (
                              <td rowSpan={4} className="p-0 align-middle">
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={2}
                                  placeholder={form.taxaGeralAgencia || '%'}
                                  value={form.passeioExclusivo[0].comissaoPct}
                                  onChange={(e) => handleComissaoPctChangePax1To4(e.target.value, 'nacional')}
                                  onBlur={() => handleComissaoBlurPax1To4('nacional')}
                                  className="w-full min-w-0 py-2 px-3 rounded border border-gray-300 bg-white focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm shadow-sm"
                                />
                              </td>
                            ) : null}
                            {pax >= 5 ? (
                              <>
                                <td className="py-2 px-3 text-sm text-center tabular-nums rounded border border-blue-200 bg-blue-100 text-gray-700">
                                  {Number.isFinite(diariaGuiaBrl) ? (
                                    <span>
                                      {formatBrl(diariaGuiaBrl)}
                                      {Number.isFinite(comissaoBrl) && (
                                        <span className="text-blue-600 ml-1">({formatBrl(comissaoBrl)})</span>
                                      )}
                                    </span>
                                  ) : '—'}
                                </td>
                                <td className="py-2 px-3 text-sm text-center tabular-nums rounded border border-orange-200 bg-orange-100 text-orange-900">
                                  {Number.isFinite(diariaGuiaBrlRight) ? (
                                    <span>
                                      {formatBrl(diariaGuiaBrlRight)}
                                      {Number.isFinite(comissaoBrlRight) && (
                                        <span className="text-blue-600 ml-1">({formatBrl(comissaoBrlRight)})</span>
                                      )}
                                    </span>
                                  ) : '—'}
                                </td>
                              </>
                            ) : pax === 1 ? (
                              <td rowSpan={4} colSpan={2} className="py-2 px-3 text-sm text-gray-500 text-center tabular-nums rounded border border-gray-200 bg-gray-100 align-middle">
                                {Number.isFinite(diariaGuiaBrl) ? (
                                  <span>
                                    {formatBrl(diariaGuiaBrl)}
                                    {Number.isFinite(comissaoBrl) && (
                                      <span className="text-blue-600 ml-1">({formatBrl(comissaoBrl)})</span>
                                    )}
                                  </span>
                                ) : '—'}
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {formTab === 'bilingue' && (
                  <section className="bg-gray-50 rounded-xl p-6 border border-gray-200 w-full min-w-0 overflow-hidden">
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <span className="text-2xl">🇺🇸</span> Bilingue
                      </h3>
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-blue-800 whitespace-nowrap">Aditivo Guia bilíngue (%)</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="%"
                          value={form.aditivoGuiaBilingue}
                          onChange={(e) => updateForm('aditivoGuiaBilingue', e.target.value)}
                          className="w-24 py-2 px-3 rounded border border-blue-300 bg-blue-50 focus:ring-2 focus:ring-blue-400 focus:border-blue-500 text-sm text-blue-900"
                        />
                      </div>
                    </div>
                    <div className="w-full min-w-0 overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr>
                            <th className="py-2 px-3 font-semibold text-sm text-blue-800 bg-blue-100 rounded text-left">PAX</th>
                            <th className="py-2 px-3 font-semibold text-sm text-blue-800 text-center bg-blue-100 rounded">
                              <span className="block text-xs text-blue-600 font-normal">BRL</span>
                              Individual
                            </th>
                            <th className="py-2 px-3 font-semibold text-sm text-blue-800 text-center bg-blue-100 rounded" colSpan={2}>
                              <span className="block text-xs text-blue-600 font-normal">BRL</span>
                              Diária Plataforma Exclusivo | Excursão
                            </th>
                            <th className="py-2 px-3 font-semibold text-sm text-blue-800 text-center bg-blue-100 rounded">
                              <span className="block text-xs text-blue-600 font-normal">%</span>
                              Taxa Plataforma
                            </th>
                            <th className="py-2 px-3 font-semibold text-sm text-blue-800 text-center bg-blue-100 rounded" colSpan={2}>
                              <span className="block text-xs text-blue-600 font-normal">BRL</span>
                              Diária Guia Exclusiva | Excursão
                            </th>
                          </tr>
                          <tr className="text-xs font-medium text-blue-700 bg-blue-50 rounded">
                            <th className="py-1 px-3" />
                            <th className="py-1 px-3 text-center" />
                            <th className="py-1 px-3 text-center" colSpan={2} />
                            <th className="py-1 px-3 text-center" />
                            <th className="py-1 px-3 text-center" colSpan={2} />
                          </tr>
                        </thead>
                        <tbody>
                      {form.passeioExclusivo.map((row, i) => {
                        const pax = i + 1;
                        const diariaMinimaNum = form.diariaMinima.trim() ? parseFloat(form.diariaMinima.replace(',', '.')) : NaN;
                        const excursaoIndividualNum = form.excursaoIndividual.brl.trim() ? parseFloat(form.excursaoIndividual.brl.replace(',', '.')) : NaN;
                        const individualNacionalBrl = pax <= 4 && Number.isFinite(diariaMinimaNum)
                          ? diariaMinimaNum / pax
                          : pax >= 5 && Number.isFinite(excursaoIndividualNum)
                            ? excursaoIndividualNum
                            : NaN;
                        const aditivoPct = form.aditivoGuiaBilingue.trim() ? parseFloat(form.aditivoGuiaBilingue.replace(',', '.')) : NaN;
                        const individualBilingueBrl = Number.isFinite(individualNacionalBrl) && Number.isFinite(aditivoPct)
                          ? individualNacionalBrl * (1 + aditivoPct / 100)
                          : NaN;
                        const individualPax4BilingueBrl = Number.isFinite(diariaMinimaNum) && Number.isFinite(aditivoPct)
                          ? (diariaMinimaNum / 4) * (1 + aditivoPct / 100)
                          : NaN;
                        const diariaSistemaBilingueBrl = Number.isFinite(individualBilingueBrl) ? individualBilingueBrl * pax : NaN;
                        const exclusivoPax5PlusBilingueBrl = pax >= 5 && Number.isFinite(individualPax4BilingueBrl) ? individualPax4BilingueBrl * pax : NaN;
                        const exclusivoPax5PlusBilingueBrlRight = pax >= 5 && Number.isFinite(individualBilingueBrl) ? individualBilingueBrl * pax : NaN;
                        const diariaSistemaParaDescontoBil = pax >= 5 && Number.isFinite(exclusivoPax5PlusBilingueBrl) ? exclusivoPax5PlusBilingueBrl : diariaSistemaBilingueBrl;
                        const descontoGuiaPct = globalConfig.descontoGuia?.trim() ? parseFloat(globalConfig.descontoGuia.replace(',', '.')) : NaN;
                        const descontoCompartilhadoBrlRaw = Number.isFinite(diariaSistemaParaDescontoBil) && Number.isFinite(descontoGuiaPct) ? diariaSistemaParaDescontoBil * (descontoGuiaPct / 100) : NaN;
                        const descontoCompartilhadoBrl = Number.isFinite(descontoCompartilhadoBrlRaw) ? Math.ceil(descontoCompartilhadoBrlRaw) : NaN;
                        const compartilhadoBrl = Number.isFinite(diariaSistemaParaDescontoBil) && Number.isFinite(descontoCompartilhadoBrl) ? diariaSistemaParaDescontoBil - descontoCompartilhadoBrl : NaN;
                        const pctComissao = row.comissaoPctBilingue.trim()
                          ? parseFloat(row.comissaoPctBilingue.replace(',', '.'))
                          : (form.taxaGeralAgencia.trim() ? parseFloat(form.taxaGeralAgencia.replace(',', '.')) : NaN);
                        const diariaSistemaParaComissaoBil = pax >= 5 && Number.isFinite(exclusivoPax5PlusBilingueBrl) ? exclusivoPax5PlusBilingueBrl : diariaSistemaBilingueBrl;
                        const comissaoBilingueBrl = Number.isFinite(diariaSistemaParaComissaoBil) && Number.isFinite(pctComissao)
                          ? diariaSistemaParaComissaoBil * (pctComissao / 100)
                          : NaN;
                        const diariaGuiaBilingueBrl = Number.isFinite(diariaSistemaParaComissaoBil) && Number.isFinite(comissaoBilingueBrl)
                          ? diariaSistemaParaComissaoBil - comissaoBilingueBrl
                          : NaN;
                        const comissaoBilingueBrlRight = pax >= 5 && Number.isFinite(exclusivoPax5PlusBilingueBrlRight) && Number.isFinite(pctComissao)
                          ? exclusivoPax5PlusBilingueBrlRight * (pctComissao / 100)
                          : NaN;
                        const diariaGuiaBilingueBrlRight = pax >= 5 && Number.isFinite(exclusivoPax5PlusBilingueBrlRight) && Number.isFinite(comissaoBilingueBrlRight)
                          ? exclusivoPax5PlusBilingueBrlRight - comissaoBilingueBrlRight
                          : NaN;
                        const comissaoCompartilhadoBrl = Number.isFinite(compartilhadoBrl) && Number.isFinite(pctComissao) ? compartilhadoBrl * (pctComissao / 100) : NaN;
                        const diariaGuiaCompartilhadoBrl = Number.isFinite(compartilhadoBrl) && Number.isFinite(comissaoCompartilhadoBrl) ? compartilhadoBrl - comissaoCompartilhadoBrl : NaN;
                        return (
                          <tr key={i}>
                            <td className="py-2 px-3 text-sm font-medium text-blue-800">{pax}</td>
                            {pax <= 4 ? (
                              <td className={`py-2 px-3 text-sm text-center tabular-nums bg-blue-50 rounded border border-blue-200 ${pax === 4 ? 'text-blue-600 font-semibold' : 'text-blue-800'}`}>
                                {formatBrl(individualBilingueBrl)}
                              </td>
                            ) : pax === 5 ? (
                              <td rowSpan={8} className="py-2 px-3 text-sm text-center tabular-nums bg-blue-50 rounded border border-blue-200 text-orange-600 font-semibold align-middle">
                                {formatBrl(individualBilingueBrl)}
                              </td>
                            ) : null}
                            {pax >= 5 ? (
                              <>
                                <td className="py-2 px-3 text-sm text-blue-800 text-center tabular-nums rounded border border-blue-200 bg-blue-100">
                                  {formatBrl(exclusivoPax5PlusBilingueBrl)}
                                </td>
                                <td className="py-2 px-3 text-sm text-blue-800 text-center tabular-nums rounded border border-orange-200 bg-orange-100">
                                  {formatBrl(exclusivoPax5PlusBilingueBrlRight)}
                                </td>
                              </>
                            ) : pax === 1 ? (
                              <td rowSpan={4} colSpan={2} className="py-2 px-3 text-sm text-blue-800 text-center tabular-nums rounded border border-blue-200 bg-blue-50 align-middle">
                                {formatBrl(diariaSistemaBilingueBrl)}
                              </td>
                            ) : null}
                            {pax >= 5 ? (
                              <td className="p-0">
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={2}
                                  placeholder={form.taxaGeralAgencia || '%'}
                                  value={row.comissaoPctBilingue}
                                  onChange={(e) => handleComissaoPctChange(i, e.target.value, 'bilingue')}
                                  onBlur={() => handleComissaoBlur(i, 'bilingue')}
                                  className="w-full min-w-0 py-2 px-3 rounded border border-blue-300 bg-white focus:ring-2 focus:ring-blue-400 focus:border-blue-500 text-sm text-blue-900 shadow-sm"
                                />
                              </td>
                            ) : pax === 1 ? (
                              <td rowSpan={4} className="p-0 align-middle">
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={2}
                                  placeholder={form.taxaGeralAgencia || '%'}
                                  value={form.passeioExclusivo[0].comissaoPctBilingue}
                                  onChange={(e) => handleComissaoPctChangePax1To4(e.target.value, 'bilingue')}
                                  onBlur={() => handleComissaoBlurPax1To4('bilingue')}
                                  className="w-full min-w-0 py-2 px-3 rounded border border-blue-300 bg-white focus:ring-2 focus:ring-blue-400 focus:border-blue-500 text-sm text-blue-900 shadow-sm"
                                />
                              </td>
                            ) : null}
                            {pax >= 5 ? (
                              <>
                                <td className="py-2 px-3 text-sm text-center tabular-nums rounded border border-blue-200 bg-blue-100 text-blue-800">
                                  {Number.isFinite(diariaGuiaBilingueBrl) ? (
                                    <span>
                                      {formatBrl(diariaGuiaBilingueBrl)}
                                      {Number.isFinite(comissaoBilingueBrl) && (
                                        <span className="text-blue-600 ml-1">({formatBrl(comissaoBilingueBrl)})</span>
                                      )}
                                    </span>
                                  ) : '—'}
                                </td>
                                <td className="py-2 px-3 text-sm text-center tabular-nums rounded border border-orange-200 bg-orange-100 text-orange-900">
                                  {Number.isFinite(diariaGuiaBilingueBrlRight) ? (
                                    <span>
                                      {formatBrl(diariaGuiaBilingueBrlRight)}
                                      {Number.isFinite(comissaoBilingueBrlRight) && (
                                        <span className="text-blue-600 ml-1">({formatBrl(comissaoBilingueBrlRight)})</span>
                                      )}
                                    </span>
                                  ) : '—'}
                                </td>
                              </>
                            ) : pax === 1 ? (
                              <td rowSpan={4} colSpan={2} className="py-2 px-3 text-sm text-center tabular-nums rounded border border-blue-200 bg-blue-50 text-blue-800 align-middle">
                                {Number.isFinite(diariaGuiaBilingueBrl) ? (
                                  <span>
                                    {formatBrl(diariaGuiaBilingueBrl)}
                                    {Number.isFinite(comissaoBilingueBrl) && (
                                      <span className="text-blue-600 ml-1">({formatBrl(comissaoBilingueBrl)})</span>
                                    )}
                                  </span>
                                ) : '—'}
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 bg-white">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium"
                >
                  Salvar tarifa
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </AdminLayout>
  );
}
